import { baseApi } from '../baseApi';

export const adminExerciseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Admin Exercise CRUD
    createExercise: builder.mutation({
      query: (exerciseData) => ({
        url: '/admin/exercises',
        method: 'POST',
        body: exerciseData,
      }),
      invalidatesTags: ['Exercise'],
    }),
    getAdminExercises: builder.query({
      query: (params = {}) => ({
        url: '/admin/exercises',
        params: {
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: ['Exercise'],
    }),
    getAdminExerciseById: builder.query({
      query: (exerciseId) => `/admin/exercises/${exerciseId}`,
      providesTags: (result, error, exerciseId) => [{ type: 'ExerciseDetail', id: exerciseId }],
    }),
    updateExercise: builder.mutation({
      query: ({ exerciseId, ...exerciseData }) => ({
        url: `/admin/exercises/${exerciseId}`,
        method: 'PUT',
        body: exerciseData,
      }),
      invalidatesTags: ['Exercise']
    }),
    deleteExercise: builder.mutation({
      query: (exerciseId) => ({
        url: `/admin/exercises/${exerciseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exercise'],
    }),

    // Bulk-add curated questions parsed from PDF to an Exercise Day's
    // container. `target` decides which container (attendance-test |
    // lc-exercise | rc-exercise). For LC/RC, every question must carry a
    // `partNumber` — either per-item or via top-level `partNumber`.
    bulkAddExerciseDayQuestions: builder.mutation({
      query: ({ dayId, target, partNumber, questions }) => ({
        url: `/admin/exercise-days/${dayId}/questions/bulk`,
        method: 'POST',
        body: {
          target,
          ...(partNumber !== undefined && partNumber !== null
            ? { partNumber }
            : {}),
          questions,
        },
      }),
      invalidatesTags: (result, error, { dayId }) => [
        'Exercise',
        { type: 'AttendanceTestQuestion', id: dayId },
        { type: 'ExerciseQuestion', id: `${dayId}-attendanceTest` },
        { type: 'ExerciseQuestion', id: `${dayId}-rcExercise` },
        { type: 'ExerciseQuestion', id: `${dayId}-lcExercise` },
      ],
    }),

    // Exercise Day Management
    addExerciseDay: builder.mutation({
      query: ({ exerciseId, dayData }) => ({
        url: `/admin/exercises/${exerciseId}/days`,
        method: 'POST',
        body: dayData,
      }),
      invalidatesTags: ['Exercise'],
    }),
    updateExerciseDay: builder.mutation({
      query: ({ exerciseId, dayId, ...dayData }) => ({
        url: `/admin/exercises/${exerciseId}/days/${dayId}`,
        method: 'PUT',
        body: dayData,
      }),
      invalidatesTags: ['Exercise'],
    }),
    removeExerciseDay: builder.mutation({
      query: ({ exerciseId, dayId }) => ({
        url: `/admin/exercises/${exerciseId}/days/${dayId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exercise'],
    }),

    // Vocabulary Management
    getVocabularyWords: builder.query({
      query: ({ dayId, page, limit }) => ({
        url: `/admin/days/${dayId}/vocabulary`,
        params: {
          page,
          limit,
        },
      }),
      providesTags: (result, error, { dayId }) => [
        { type: 'VocabularyWord', id: `${dayId}` }
      ],
    }),
    createVocabularyWord: builder.mutation({
      query: ({ dayId, wordData }) => ({
        url: `/admin/days/${dayId}/vocabulary/words`,
        method: 'POST',
        body: wordData,
      }),
      invalidatesTags: (result, error, { dayId }) => [
        { type: 'VocabularyWord', id: `${dayId}` }
      ],
    }),
    updateVocabularyWord: builder.mutation({
      query: ({ dayId, wordId, wordData }) => ({
        url: `/admin/days/${dayId}/vocabulary/words/${wordId}`,
        method: 'PUT',
        body: wordData,
      }),
      invalidatesTags: (result, error, { dayId }) => [
        { type: 'VocabularyWord', id: `${dayId}` }
      ],
    }),
    deleteVocabularyWord: builder.mutation({
      query: ({ dayId, wordId }) => ({
        url: `/admin/days/${dayId}/vocabulary/words/${wordId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { dayId }) => [
        { type: 'VocabularyWord', id: `${dayId}` }
      ],
    }),

    // Vocabulary Question Management (Section-based)
    getVocabularyQuestions: builder.query({
      query: ({ dayId }) => `/admin/days/${dayId}/vocabulary/questions`,
      providesTags: (result, error, { dayId }) => [
        { type: 'VocabularyQuestion', id: `${dayId}` }
      ],
    }),
    createVocabularyQuestion: builder.mutation({
      query: ({ dayId, questionData }) => ({
        url: `/admin/days/${dayId}/vocabulary/questions`,
        method: 'POST',
        body: questionData,
      }),
      invalidatesTags: (result, error, { dayId }) => [
        { type: 'VocabularyQuestion', id: `${dayId}` }
      ],
    }),
    updateVocabularyQuestion: builder.mutation({
      query: ({ questionId, questionData }) => ({
        url: `/admin/questions/${questionId}`,
        method: 'PUT',
        body: questionData,
      }),
      invalidatesTags: ['VocabularyQuestion'],
    }),
    deleteVocabularyQuestion: builder.mutation({
      query: (questionId) => ({
        url: `/admin/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VocabularyQuestion'],
    }),

    // LC/RC Exercise Part Management
    createExercisePart: builder.mutation({
      query: ({ dayId, partData }) => ({
        url: `/admin/exercise-days/${dayId}/exercise-parts`,
        method: 'POST',
        body: partData,
      }),
      invalidatesTags: ['ExerciseDay'],
    }),
    updateExercisePart: builder.mutation({
      query: ({ partId, ...partData }) => ({
        url: `/admin/exercise-parts/${partId}`,
        method: 'PUT',
        body: partData,
      }),
      invalidatesTags: ['ExercisePart'],
    }),

    // LC/RC Exercise Question Management
    getExerciseQuestions: builder.query({
      query: ({ dayId, partType }) => {
        // Converter ex: lcExercise -> lc-exercise
        const sectionType = partType.replace(/([A-Z])/g, "-$1").toLowerCase();
        const url = `/admin/days/${dayId}/sections/${sectionType}/questions`;
        console.log(url);
        return url;
      },
      providesTags: (result, error, { dayId, partType }) => [
        { type: 'ExerciseQuestion', id: `${dayId}-${partType}` }
      ],
    }),
    createExerciseQuestion: builder.mutation({
      query: ({ dayId, partType, partNumber, questionData }) => {
        // Converter ex: lcExercise -> lc-exercise
        const sectionType = partType.replace(/([A-Z])/g, "-$1").toLowerCase();
        return {
          url: `/admin/days/${dayId}/sections/${sectionType}/parts/${partNumber}/questions`,
          method: 'POST',
          body: questionData,
        };
      },
      invalidatesTags: (result, error, { dayId, partType }) => {
        const tags = [{ type: 'ExerciseQuestion', id: `${dayId}-${partType}` }];
        // Also invalidate AttendanceTestQuestion tag for attendance tests
        if (partType === 'attendanceTest') {
          tags.push({ type: 'AttendanceTestQuestion', id: `${dayId}` });
        }
        return tags;
      },
    }),
    updateExerciseQuestion: builder.mutation({
      query: ({ questionId, questionData }) => ({
        url: `/admin/questions/${questionId}`,
        method: 'PUT',
        body: questionData,
      }),
      invalidatesTags: ['ExerciseQuestion', 'AttendanceTestQuestion'],
    }),
    deleteExerciseQuestion: builder.mutation({
      query: (questionId) => ({
        url: `/admin/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExerciseQuestion', 'AttendanceTestQuestion'],
    }),

    // Section Info Management
    getSectionInfo: builder.query({
      query: ({ dayId, sectionType }) => `/admin/days/${dayId}/sections/${sectionType}/info`,
      providesTags: (result, error, { dayId, sectionType }) => [
        { type: 'SectionInfo', id: `${dayId}-${sectionType}` }
      ],
    }),
    createSectionInfo: builder.mutation({
      query: ({ dayId, sectionType, sectionData }) => ({
        url: `/admin/days/${dayId}/sections/${sectionType}/info`,
        method: 'POST',
        body: sectionData,
      }),
      invalidatesTags: (result, error, { dayId, sectionType }) => [
        { type: 'SectionInfo', id: `${dayId}-${sectionType}` }
      ],
    }),
    updateSectionInfo: builder.mutation({
      query: ({ dayId, sectionType, sectionData }) => ({
        url: `/admin/days/${dayId}/sections/${sectionType}/info`,
        method: 'PUT',
        body: sectionData,
      }),
      invalidatesTags: (result, error, { dayId, sectionType }) => [
        { type: 'SectionInfo', id: `${dayId}-${sectionType}` }
      ],
    }),

    // Attendance Test Question Management (Section-based)
    // Note: Now using unified getExerciseQuestions with partType='attendanceTest'
    getAttendanceTestQuestions: builder.query({
      query: ({ dayId }) => `/admin/days/${dayId}/sections/attendance-test/questions`,
      providesTags: (result, error, { dayId }) => [
        { type: 'AttendanceTestQuestion', id: `${dayId}` }
      ],
    }),

    // Assignment Management
    assignExerciseDayToClass: builder.mutation({
      query: ({ exerciseId, dayId, classId }) => ({
        url: `/admin/exercises/${exerciseId}/days/${dayId}/assign/${classId}`,
        method: 'POST',
      }),
      invalidatesTags: ['ExerciseAssignment', 'ExerciseDayAssignment'],
    }),
    unassignExerciseDayFromClass: builder.mutation({
      query: ({ exerciseId, dayId, classId }) => ({
        url: `/admin/exercises/${exerciseId}/days/${dayId}/unassign/${classId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExerciseAssignment', 'ExerciseDayAssignment'],
    }),
    getExerciseDayAssignedClasses: builder.query({
      query: ({ exerciseId, dayId }) => `/admin/exercises/${exerciseId}/days/${dayId}/assigned-classes`,
      providesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'ExerciseDayAssignment', id: `${exerciseId}-${dayId}` }
      ],
    }),
    getClassExerciseAssignments: builder.query({
      query: (classId) => `/admin/classes/${classId}/exercise-assignments`,
      providesTags: (result, error, classId) => [
        { type: 'ExerciseAssignment', id: classId }
      ],
    }),

    // Dialogue Question Group Management
    createDialogueQuestionGroup: builder.mutation({
      query: (dialogueData) => ({
        url: '/admin/dialogue-questions',
        method: 'POST',
        body: dialogueData,
      }),
      invalidatesTags: ['ExerciseQuestion', 'DialogueQuestion'],
    }),
    getDialogueQuestionGroup: builder.query({
      query: (parentQuestionId) => `/admin/dialogue-questions/${parentQuestionId}`,
      providesTags: (result, error, parentQuestionId) => [
        { type: 'DialogueQuestion', id: parentQuestionId }
      ],
    }),
    updateDialogueQuestionGroup: builder.mutation({
      query: ({ parentQuestionId, dialogueData }) => ({
        url: `/admin/dialogue-questions/${parentQuestionId}`,
        method: 'PUT',
        body: dialogueData, // Can be FormData or regular object
      }),
      invalidatesTags: (result, error, { parentQuestionId }) => [
        { type: 'DialogueQuestion', id: parentQuestionId },
        'ExerciseQuestion',
      ],
    }),
    deleteDialogueQuestionGroup: builder.mutation({
      query: (parentQuestionId) => ({
        url: `/admin/dialogue-questions/${parentQuestionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExerciseQuestion', 'DialogueQuestion'],
    }),
    getDialogueQuestionsBySection: builder.query({
      query: ({ section, exerciseDay, partNumber }) => ({
        url: `/admin/dialogue-questions/section/${section}`,
        params: { exerciseDay, partNumber },
      }),
      providesTags: ['DialogueQuestion'],
    }),
  }),
});

export const {
  // Admin Exercise CRUD
  useCreateExerciseMutation,
  useGetAdminExercisesQuery,
  useLazyGetAdminExercisesQuery,
  useGetAdminExerciseByIdQuery,
  useLazyGetAdminExerciseByIdQuery,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  
  // Exercise Day Management
  useAddExerciseDayMutation,
  useUpdateExerciseDayMutation,
  useRemoveExerciseDayMutation,
  
  // Vocabulary Management
  useGetVocabularyWordsQuery,
  useCreateVocabularyWordMutation,
  useUpdateVocabularyWordMutation,
  useDeleteVocabularyWordMutation,
  
  // Vocabulary Question Management
  useGetVocabularyQuestionsQuery,
  useLazyGetVocabularyQuestionsQuery,
  useCreateVocabularyQuestionMutation,
  useUpdateVocabularyQuestionMutation,
  useDeleteVocabularyQuestionMutation,
  
  // LC/RC Exercise Part Management
  useCreateExercisePartMutation,
  useUpdateExercisePartMutation,
  
  // LC/RC Exercise Question Management
  useGetExerciseQuestionsQuery,
  useCreateExerciseQuestionMutation,
  useUpdateExerciseQuestionMutation,
  useDeleteExerciseQuestionMutation,
  
  // Attendance Test Question Management
  // Note: Now using unified exercise question mutations (create/update/delete)
  useGetAttendanceTestQuestionsQuery,
  
  // Assignment Management
  useAssignExerciseDayToClassMutation,
  useUnassignExerciseDayFromClassMutation,
  useGetExerciseDayAssignedClassesQuery,
  useGetClassExerciseAssignmentsQuery,
  
  // Section Info Management
  useGetSectionInfoQuery,
  useCreateSectionInfoMutation,
  useUpdateSectionInfoMutation,
  
  // Dialogue Question Group Management
  useCreateDialogueQuestionGroupMutation,
  useGetDialogueQuestionGroupQuery,
  useUpdateDialogueQuestionGroupMutation,
  useDeleteDialogueQuestionGroupMutation,
  useGetDialogueQuestionsBySectionQuery,
  useLazyGetDialogueQuestionGroupQuery,

  // PDF upload
  useBulkAddExerciseDayQuestionsMutation,
} = adminExerciseApi;
